import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { omit } from "lodash";

import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import BillMeterReadingFormItems from "./BillMeterReadingFormItems";

import {
  useGetMeterReadingDataByMeterIdQuery,
  useGetFpppaChargesByBillCycleId,
  usePostUpdatedMeterReadingsMutation,
  usePutMeterReadingMutation,
} from "apis/MeterReading";

import { getImageUrl } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getMeterReadingValidation } from "utils/getMeterReadingValidation";
import FPPPAChargesForm from "./FPPPAChargesForm";

const keysToDelete = [
  "activeEnergyDifference",
  "apparentEnergyDifference",
  "reactiveEnergyDifference",
  "peakHourEnergyTOD1Difference",
  "peakHourEnergyTOD5Difference",
  "nightHourEnergyDifference",
  "exportEnergyDifference",
  "setOffImportEnergyTOD1Difference",
  "setOffExportEnergyTOD1Difference",
  "setOffImportEnergyTOD3Difference",
  "setOffExportEnergyTOD3Difference",
  "touDiscountEnergyDifference",
  "meterNumber",
];

const meterReadingValidationSchema = Yup.object({
  meterReadingsData: Yup.array().of(
    Yup.object().shape({
      activeEnergy: getMeterReadingValidation(
        "activeEnergyDifference",
        "Active energy",
        "Active energy difference"
      ),
      meterReadingDate: Yup.date().required("Meter reading date is required"),
      apparentEnergy: getMeterReadingValidation(
        "apparentEnergyDifference",
        "Apparent energy",
        "Apparent energy difference"
      ),
      reactiveEnergy: getMeterReadingValidation(
        "reactiveEnergyDifference",
        "Reactive energy",
        "Reactive energy difference"
      ),
      exportEnergy: getMeterReadingValidation(
        "exportEnergyDifference",
        "Export energy",
        "Export energy difference"
      ),
      maxDemand: Yup.number().required("Max demand is required"),
      peakHourEnergyTOD1: getMeterReadingValidation(
        "peakHourEnergyTOD1Difference",
        "Peak hour energy TOD1",
        "Peak hour energy TOD1 difference"
      ),
      peakHourEnergyTOD5: getMeterReadingValidation(
        "peakHourEnergyTOD5Difference",
        "Peak hour energy TOD5",
        "Peak hour energy TOD5 difference"
      ),
      nightHourEnergy: getMeterReadingValidation(
        "nightHourEnergyDifference",
        "Night hour energy",
        "Night hour energy difference"
      ),
      setOffExportEnergyTOD1: getMeterReadingValidation(
        "setOffExportEnergyTOD1Difference",
        "Set off import energy TOD1",
        "Set off import energy TOD1 difference"
      ),
      setOffImportEnergyTOD1: getMeterReadingValidation(
        "setOffImportEnergyTOD1Difference",
        "set off export energy TOD1",
        "Set off export energy TOD1 difference"
      ),
      setOffExportEnergyTOD3: getMeterReadingValidation(
        "setOffExportEnergyTOD3Difference",
        "Set off import energy TOD3",
        "Set off import energy TOD3 difference"
      ),
      setOffImportEnergyTOD3: getMeterReadingValidation(
        "setOffImportEnergyTOD3Difference",
        "Set off export energy TOD3",
        "Set off export energy TOD3 difference"
      ),
      touDiscountEnergy:getMeterReadingValidation("touDiscountEnergyDifference","TOU Discount Energy","TOU Discount Energy Difference"),
      previousMeterReading: Yup.object({
        activeEnergy: getMeterReadingValidation(
          "activeEnergyDifference",
          "Previous meter active energy",
          "Active energy difference",
          true
        ),
        apparentEnergy: getMeterReadingValidation(
          "apparentEnergyDifference",
          "Previous meter apparent energy",
          "Apparent energy difference",
          true
        ),
        reactiveEnergy: getMeterReadingValidation(
          "reactiveEnergyDifference",
          "Previous meter reactive energy",
          "Reactive energy difference",
          true
        ),
        exportEnergy: getMeterReadingValidation(
          "exportEnergyDifference",
          "Previous meter export energy",
          "Export energy difference",
          true
        ),
        maxDemand: Yup.number().required("Max demand is required"),
        peakHourEnergyTOD1: getMeterReadingValidation(
          "peakHourEnergyTOD1Difference",
          "Previous meter peak hour energy TOD1",
          "Peak hour energy TOD1 difference",
          true
        ),
        peakHourEnergyTOD5: getMeterReadingValidation(
          "peakHourEnergyTOD5Difference",
          "Previous meter peak hour energy TOD5",
          "Peak hour energy TOD5 difference",
          true
        ),
        nightHourEnergy: getMeterReadingValidation(
          "nightHourEnergyDifference",
          "Previous meter night hour energy",
          "Night hour energy difference",
          true
        ),
        setOffExportEnergyTOD1: getMeterReadingValidation(
          "setOffExportEnergyTOD1Difference",
          "Set off import energy TOD1",
          "Set off import energy TOD1 difference",
          true
        ),
        setOffImportEnergyTOD1: getMeterReadingValidation(
          "setOffImportEnergyTOD1Difference",
          "Set off export energy TOD1",
          "Set off export energy TOD1 difference",
          true
        ),
        setOffExportEnergyTOD3: getMeterReadingValidation(
          "setOffExportEnergyTOD3Difference",
          "Set off import energy TOD3",
          "Set off import energy TOD3 difference",
          true
        ),
        setOffImportEnergyTOD3: getMeterReadingValidation(
          "setOffImportEnergyTOD3Difference",
          "Set off export energy TOD3",
          "Set off export energy TOD3 difference",
          true
        ),
        touDiscountEnergy:getMeterReadingValidation(
          "touDiscountEnergyDifference",
          "TOU Discount Energy",
          "TOU Discount Energy Difference",
          true
        )
      }),
    })
  ),
});

const BillMeterReadingForm = ({
  utilityConsumerId,
  handleClose,
  billCycleId,
  t,
}) => {
  const [documentToPreview, setDocumentToPreview] = useState("");

  const [
    getUpdatedBillCalculations,
    { isLoading: isUpdatedBillCalculationLoading },
  ] = usePostUpdatedMeterReadingsMutation();

  const [updateMeterReadings, { isLoading: isMeterReadingsUpdating }] =
    usePutMeterReadingMutation();

  const {
    data: { data: { utilityConsumerMeterReadings, utilityConsumer } = {} } = {},
    isLoading: isConsumerMeterReadingLoading,
  } = useGetMeterReadingDataByMeterIdQuery(billCycleId, utilityConsumerId);

    const {
    data: { data: fpppaCharges = [] } = {},
    isLoading: isFpppaChargesLoading,
  } = useGetFpppaChargesByBillCycleId(billCycleId);

  const initialValues = useMemo(
    () => ({
      meterReadingsData:
        utilityConsumerMeterReadings?.map((item) => ({
          id: item?.id ?? 0,
          utilityConsumerId: utilityConsumer?.id ?? 0,
          meterNumber: item?.meter?.srNo,
          meterId: item?.meter?.id ?? 0,
          activeEnergy: item?.activeEnergy ?? "",
          apparentEnergy: item?.apparentEnergy ?? "",
          reactiveEnergy: item?.reactiveEnergy ?? "",
          exportEnergy: item?.exportEnergy ?? "",
          maxDemand: item?.maxDemand ?? "",
          exportEnergyDifference: item?.exportEnergyDifference ?? "",
          peakHourEnergyTOD1: item?.peakHourEnergyTOD1 ?? "",
          peakHourEnergyTOD5: item?.peakHourEnergyTOD5 ?? "",
          nightHourEnergy: item?.nightHourEnergy ?? "",
          setOffExportEnergyTOD1: item?.setOffExportEnergyTOD1 ?? 0,
          setOffImportEnergyTOD1: item?.setOffImportEnergyTOD1 ?? 0,
          setOffExportEnergyTOD3: item?.setOffExportEnergyTOD3 ?? 0,
          setOffImportEnergyTOD3: item?.setOffImportEnergyTOD3 ?? 0,
          touDiscountEnergy:item?.touDiscountEnergy ?? 0,
          activeEnergyDifference: item?.activeEnergyDifference ?? 0,
          apparentEnergyDifference: item?.apparentEnergyDifference ?? 0,
          reactiveEnergyDifference: item?.reactiveEnergyDifference ?? 0,
          peakHourEnergyTOD1Difference: item?.peakHourEnergyTOD1Difference ?? 0,
          peakHourEnergyTOD5Difference: item?.peakHourEnergyTOD5Difference ?? 0,
          nightHourEnergyDifference: item?.nightHourEnergyDifference ?? 0,
          setOffExportEnergyTOD1Difference:
            item?.setOffExportEnergyTOD1Difference ?? 0,
          setOffImportEnergyTOD1Difference:
            item?.setOffImportEnergyTOD1Difference ?? 0,
          setOffExportEnergyTOD3Difference:
            item?.setOffExportEnergyTOD3Difference ?? 0,
          setOffImportEnergyTOD3Difference:
            item?.setOffImportEnergyTOD3Difference ?? 0,
          touDiscountEnergyDifference:item?.touDiscountEnergyDifference ?? 0,
          tariffId: item?.tariffId ?? "",
          applicantTypeId: item?.applicantTypeId ?? "",
          supplyVoltage: item?.supplyVoltage ?? "",
          totalPowerRequirement: item?.totalPowerRequirement ?? "",
          greenPower: item?.greenPower ?? "",
          isBPL: item?.isBPL ?? false,
          meterReadingDate: item?.meterReadingDate
            ? convertUTCDateToLocalDate(item.meterReadingDate)
            : "",
          previousMeterReading: {
            id: item?.previousMeterReading?.id ?? 0,
            activeEnergy: item?.previousMeterReading?.activeEnergy ?? 0,
            apparentEnergy: item?.previousMeterReading?.apparentEnergy ?? 0,
            reactiveEnergy: item?.previousMeterReading?.reactiveEnergy ?? 0,
            exportEnergy: item?.previousMeterReading?.exportEnergy ?? 0,
            maxDemand: item?.previousMeterReading?.maxDemand ?? 0,
            peakHourEnergyTOD1:
              item?.previousMeterReading?.peakHourEnergyTOD1 ?? 0,
            peakHourEnergyTOD5:
              item?.previousMeterReading?.peakHourEnergyTOD5 ?? 0,
            setOffExportEnergyTOD1:
              item?.previousMeterReading?.setOffExportEnergyTOD1 ?? 0,
            setOffImportEnergyTOD1:
              item?.previousMeterReading?.setOffImportEnergyTOD1 ?? 0,
            setOffExportEnergyTOD3:
              item?.previousMeterReading?.setOffExportEnergyTOD3 ?? 0,
            setOffImportEnergyTOD3:
              item?.previousMeterReading?.setOffImportEnergyTOD3 ?? 0,
            touDiscountEnergy:item?.previousMeterReading?.touDiscountEnergy ?? 0,
            nightHourEnergy: item?.previousMeterReading?.nightHourEnergy ?? 0,
            meterId: item?.previousMeterReading?.meter?.id ?? 0,
            utilityConsumerId: utilityConsumer?.id ?? 0,
            tariffId: item?.previousMeterReading?.tariffId ?? "",
            applicantTypeId: item?.previousMeterReading?.applicantTypeId ?? "",
            meterReadingDate: item?.previousMeterReading?.meterReadingDate
              ? convertUTCDateToLocalDate(
                  item.previousMeterReading.meterReadingDate
                ).format("YYYY-MM-DD")
              : "",
            totalPowerRequirement:
              item?.previousMeterReading?.totalPowerRequirement ?? "",
            supplyVoltage: item?.previousMeterReading?.supplyVoltage ?? "",
            isBPL: item?.previousMeterReading?.isBPL ?? false,
            greenPower: item?.previousMeterReading?.greenPower ?? "",
          },
            adjustmentKWH: item?.adjustmentKWH ?? 0,
            adjustmentKVAH: item?.adjustmentKVAH ?? 0,
            adjustmentKVArH: item?.adjustmentKVArH ?? 0,
            adjustmentPeakHour: item?.adjustmentPeakHour ?? 0,
            adjustmentTOUDiscount: item?.adjustmentTOUDiscount ?? 0,
        })) || [],
      fpppaChargesData: fpppaCharges ?? [],
    }),
    [utilityConsumerMeterReadings, utilityConsumer]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: meterReadingValidationSchema,
    onSubmit: async (values) => {
      try {
        const meterReadings = values.meterReadingsData.map((meterReading) =>
          meterReading.previousMeterReading.id
            ? omit(
                {
                  ...meterReading,
                  adjustmentKWH: Number(meterReading.adjustmentKWH || 0),
                  adjustmentKVAH: Number(meterReading.adjustmentKVAH || 0),
                  adjustmentKVArH: Number(meterReading.adjustmentKVArH || 0),
                  adjustmentPeakHour: Number(meterReading.adjustmentPeakHour || 0),
                  adjustmentTOUDiscount: Number(meterReading.adjustmentTOUDiscount || 0),
                  meterReadingDate:
                    meterReading.meterReadingDate.format("YYYY-MM-DD"),
                },
                keysToDelete
              )
            : omit(
                {
                  ...meterReading,
                  adjustmentKWH: Number(meterReading.adjustmentKWH || 0),
                  adjustmentKVAH: Number(meterReading.adjustmentKVAH || 0),
                  adjustmentKVArH: Number(meterReading.adjustmentKVArH || 0),
                  adjustmentPeakHour: Number(meterReading.adjustmentPeakHour || 0),
                  adjustmentTOUDiscount: Number(meterReading.adjustmentTOUDiscount || 0),
                  meterReadingDate:
                    meterReading.meterReadingDate.format("YYYY-MM-DD"),
                },
                [...keysToDelete, "previousMeterReading"]
              )
        );

        const payload = {meterReadings,FPPPACharges:values.fpppaChargesData};

        const { data: { response: url } = {} } =
          await getUpdatedBillCalculations(billCycleId, payload);

        setDocumentToPreview({
          url: getImageUrl(url),
          documentTypeName: "Bill",
          documentType: "pdf",
          documentName: "bill",
        });
      } catch (error) {}
    },
  });

  const { values, handleSubmit } = formik;

  const approveBtnHandler = async () => {
    try {
      const meterReadings = values.meterReadingsData.map((meterReading) =>
        meterReading.previousMeterReading.id
          ? omit(
              {
                ...meterReading,
                adjustmentKWH: Number(meterReading.adjustmentKWH || 0),
                adjustmentKVAH: Number(meterReading.adjustmentKVAH || 0),
                adjustmentKVArH: Number(meterReading.adjustmentKVArH || 0),
                adjustmentPeakHour: Number(meterReading.adjustmentPeakHour || 0),
                adjustmentTOUDiscount: Number(meterReading.adjustmentTOUDiscount || 0),
                meterReadingDate:
                  meterReading.meterReadingDate.format("YYYY-MM-DD"),
              },
              keysToDelete
            )
          : omit(
              {
                ...meterReading,
                adjustmentKWH: Number(meterReading.adjustmentKWH || 0),
                adjustmentKVAH: Number(meterReading.adjustmentKVAH || 0),
                adjustmentKVArH: Number(meterReading.adjustmentKVArH || 0),
                adjustmentPeakHour: Number(meterReading.adjustmentPeakHour || 0),
                adjustmentTOUDiscount: Number(meterReading.adjustmentTOUDiscount || 0),
                meterReadingDate:
                  meterReading.meterReadingDate.format("YYYY-MM-DD"),
              },
              [...keysToDelete, "previousMeterReading"]
            )
      );

      const payload = {meterReadings,FPPPACharges:values.fpppaChargesData};
      await updateMeterReadings(billCycleId, payload);
      handleClose();
    } catch (error) {}
  };

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          actionButton={
            <Button
              color="success"
              onClick={approveBtnHandler}
              variant="contained"
              disabled={isMeterReadingsUpdating}
            >
              {t("Approve")}
              {isMeterReadingsUpdating && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}

      {(isConsumerMeterReadingLoading||isFpppaChargesLoading) ? (
        <Backdrop open={isConsumerMeterReadingLoading||isFpppaChargesLoading} />
      ) : (
        <Modal
          isOpen
          handleClose={handleClose}
          title="Edit Meter Readings"
          maxWidth="md"
        >
          <Grid container columnGap={1} className="mt-3">
            {values.meterReadingsData.map((meterReading, index) => (
              <BillMeterReadingFormItems
                meterReading={meterReading}
                formik={formik}
                index={index}
                key={index}
              />
            ))}
            <Grid
              container
              columnSpacing={1}
              className="border border-1 pt-2 pe-2 mb-2"
            >
              <FPPPAChargesForm fppaChargesData={values.fpppaChargesData} formik={formik} isMeterReadingForm/>
            </Grid>
            <Box className="d-block ms-auto" mt={2}>
              <Button
                variant="contained"
                size="medium"
                color="primary"
                onClick={handleSubmit}
                disabled={isUpdatedBillCalculationLoading}
              >
                {t("Preview")}
                {isUpdatedBillCalculationLoading && (
                  <CircularProgress size={20} />
                )}
              </Button>
            </Box>
          </Grid>
        </Modal>
      )}
    </>
  );
};

BillMeterReadingForm.propTypes = {
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
  billCycleId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(BillMeterReadingForm);
